import { Simulator } from './arena.js';

// Register service worker if available
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(new URL('./service-worker.js', window.location.href));
  });
}

const ONBOARDING_SEEN_KEY = 'maneuver.onboarding.seen.v1';

/**
 * @class GameManager
 * Handles high-level application state and mode selection.
 */
class GameManager {
  constructor() {
    this.menuOverlay = document.getElementById('main-menu');
    this.btnSimulator = document.getElementById('btn-simulator');
    this.onboardingOverlay = document.getElementById('onboarding-overlay');
    this.btnHelp = document.getElementById('btn-help');
    this.onboardingDismiss = document.getElementById('onboarding-dismiss');
    this._onboardingShownThisSession = false;

    // Bind methods
    this.startSimulatorMode = this.startSimulatorMode.bind(this);
    this._onOnboardingKeydown = this._onOnboardingKeydown.bind(this);

    this._attachListeners();
    this._checkURLParams();
  }

  _attachListeners() {
    this.btnSimulator?.addEventListener('click', this.startSimulatorMode);

    // Onboarding: help button re-opens it; backdrop click and "Got it" dismiss it.
    this.btnHelp?.addEventListener('click', () => this.showOnboarding());
    this.onboardingDismiss?.addEventListener('click', () => this.hideOnboarding());
    this.onboardingOverlay?.addEventListener('click', (e) => {
      if (e.target === this.onboardingOverlay) this.hideOnboarding();
    });

    // Main Menu navigation via Logo
    const logo = document.querySelector('.maneuverlogo');
    if (logo) {
      logo.style.cursor = 'pointer';
      // pointerup fires reliably on touch (a synthesized click can be suppressed
      // on mobile), so the logo opens the main menu on phones as well as desktop.
      logo.addEventListener('pointerup', () => {
        this.showMenu();
        if (window.sim && window.sim.isSimulationRunning) {
          window.sim.togglePlayPause();
        }
      });
    }
  }

  _checkURLParams() {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');

    if (mode === 'simulator') {
      this.startSimulatorMode();
    }
  }

  hideMenu() {
    this.menuOverlay.classList.add('hidden');
  }

  showMenu() {
    this.menuOverlay.classList.remove('hidden');
  }

  startSimulatorMode() {
    this.hideMenu();
    this.initSimulator({ mode: 'simulator' });
    this._maybeShowOnboarding();
  }

  initSimulator(config) {
    if (window.sim) {
      window.sim.destroy();
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        window.sim = new Simulator(config);
      });
    } else {
      window.sim = new Simulator(config);
    }
  }

  _maybeShowOnboarding() {
    let seen = false;
    try {
      seen = localStorage.getItem(ONBOARDING_SEEN_KEY) === '1';
    } catch (_) {
      // Private mode / storage disabled: fall back to once per session.
      seen = this._onboardingShownThisSession;
    }
    if (!seen) this.showOnboarding();
  }

  showOnboarding() {
    if (!this.onboardingOverlay) return;
    this._onboardingShownThisSession = true;
    this.onboardingOverlay.classList.remove('hidden');
    document.addEventListener('keydown', this._onOnboardingKeydown);
  }

  hideOnboarding(persist = true) {
    if (!this.onboardingOverlay) return;
    this.onboardingOverlay.classList.add('hidden');
    document.removeEventListener('keydown', this._onOnboardingKeydown);
    if (persist) {
      try {
        localStorage.setItem(ONBOARDING_SEEN_KEY, '1');
      } catch (_) {
        // Ignore write failures (private mode).
      }
    }
  }

  _onOnboardingKeydown(e) {
    if (e.key === 'Escape') this.hideOnboarding();
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  // If embedded or waiting for specific scripts (like existing logic checked window.TypeForm),
  // adapt here. For this refactor, we assume direct control.

  window.gameManager = new GameManager();

  // Existing embeds logic from previous index.html code can be adapted if actually needed,
  // but requirements suggest we control the entry via Main Menu now. 
});

